import { urlDoClipe } from '@modules/aawz-intelligence-v2/components/boas-vindas-falada/provedor-de-clipe'

// ★ O ROSTO da apresentação — quem aparece, e em que ordem (spec-009).
//
// Separado da FALA (`boas-vindas-falada.vue`) quando o componente passou de
// 500 linhas. O corte é por responsabilidade e não por tamanho: aqui está
// tudo o que decide **o que se vê**; lá, tudo o que decide **o que se ouve**.
//
// A precedência, do mais forte ao mais fraco:
//
//   rosto AO VIVO  →  clipe  →  retrato  →  nada
//
// ★ O rosto ao vivo vence porque é o único que de fato FALA esta apresentação
// (`falante-como-audio.js`): mostrar um clipe por cima dele seria esconder a
// boca que se mexe. Antes de 18/09/2026 o `<video>` do rosto morava só no
// cartão da CONVERSA, que monta depois da apresentação — o avatar falava e
// ninguém o via (medido: `agent.speak_started` recebido, 112 chunks no fio,
// `rosto visível: false`).
export const boasVindasFaladaMidiaMixin = {
  props: {
    // As faixas que voltam da sala do provedor.
    //
    // ⚠️ A de ÁUDIO não é opcional quando a de vídeo existe: com o falante
    // ligado o `<audio>` local não é montado, e é esta faixa que faz som.
    // Sem ela o avatar mexe a boca em silêncio.
    faixaDeVideo: {
      type: Object,
      default: null,
    },
    faixaDeAudio: {
      type: Object,
      default: null,
    },
  },
  computed: {
    // Ele não para no `concluida` — quando a apresentação termina, quem
    // assume é o cartão da conversa, com a MESMA faixa.
    temAoVivo() {
      return Boolean(this.faixaDeVideo) && !this.concluida
    },
    // O que o host passou vence o que o provedor buscou: quem já tem a URL na
    // mão não precisa esperar por uma segunda resposta para ver a mesma coisa.
    clipeEfetivo() {
      return this.clipe || this.clipeResolvido
    },
    // ⚠️ **A precedência mora na cadeia `v-if`/`v-else-if` do template, e só
    // lá.** Estes computeds chegaram a repeti-la (`!this.temAoVivo && ...`), e
    // a mutação provou a redundância: tirar a guarda daqui não quebrava nada,
    // porque o `v-else-if` já garante exclusividade. Duas fontes para o mesmo
    // fato só divergem — e o teste que mirava esta acabava medindo a que não
    // decide.
    temClipe() {
      return Boolean(this.clipeEfetivo)
    },
    // Entregue, o clipe PARA: um vídeo em loop eterno no alto da conversa
    // disputa a atenção com a resposta que está sendo escrita logo abaixo.
    mostrarClipe() {
      return this.temClipe && !this.concluida
    },
    temRetrato() {
      return !this.mostrarClipe && Boolean(this.retrato)
    },
  },
  watch: {
    // As faixas chegam DEPOIS da montagem (a sala abre em paralelo). ⚠️ SEM
    // `immediate`, e o `mounted` anexa DIRETO: `$nextTick` a mais na montagem
    // desloca a contagem de ticks de quem monta este componente — foi um
    // vermelho num teste que nada tinha a ver com avatar.
    faixaDeVideo: 'anexarVideoQuandoRenderizar',
    faixaDeAudio: 'anexarAudioQuandoRenderizar',
  },
  mounted() {
    this.anexarFaixa(this.faixaDeVideo, this.$refs.video)
    this.anexarFaixa(this.faixaDeAudio, this.$refs.audio)
    // Sem `await`: o clipe é enfeite, e enfeite não segura a mensagem.
    this.resolverClipe()
  },
  beforeDestroy() {
    // Faixa presa a um elemento destruído mantém o decodificador vivo e o som
    // tocando fora da tela.
    this.desanexar(this.faixaDeVideo, this.$refs.video)
    this.desanexar(this.faixaDeAudio, this.$refs.audio)
  },
  methods: {
    // `attach`/`detach` são do SDK de vídeo. Envoltos em try/catch: uma faixa
    // já encerrada estoura ali, e o desfecho certo é a tela seguir sem rosto
    // — nunca a apresentação cair.
    anexarFaixa(faixa, elemento) {
      if (!faixa || !elemento || typeof faixa.attach !== 'function') return
      try {
        faixa.attach(elemento)
      } catch (e) {
        // Sem rosto é um estado previsto; o roteiro continua na tela.
      }
    },
    desanexar(faixa, elemento) {
      if (!faixa || !elemento || typeof faixa.detach !== 'function') return
      try {
        faixa.detach(elemento)
      } catch (e) {
        // idem
      }
    },
    anexarVideoQuandoRenderizar() {
      this.$nextTick(() => this.anexarFaixa(this.faixaDeVideo, this.$refs.video))
    },
    anexarAudioQuandoRenderizar() {
      this.$nextTick(() => this.anexarFaixa(this.faixaDeAudio, this.$refs.audio))
    },
    // ★ O clipe, quando há provedor. `obter()` nunca rejeita (a degradação é o
    // contrato dele): gerando, indisponível e ausente devolvem URL vazia, e a
    // tela segue com o retrato — o mesmo caminho de quem nunca teve clipe.
    async resolverClipe() {
      if (this.clipe || !this.provedorDeClipe) return
      const resultado = await this.provedorDeClipe.obter()
      if (this._destruido) return
      this.clipeResolvido = urlDoClipe(resultado)
    },
  },
}
